/**
 * Reserve in Store Drawer
 * Theme UI only — submissions are handled by app embed via reserve-in-store:submit.
 */

(function () {
  "use strict";

  const SUBMIT_EVENT = "reserve-in-store:submit";

  const CUSTOMER_FIELDS = ["customer_name", "customer_email", "customer_phone"];

  let storesData = [];
  let searchQuery = "";
  let activeDrawer = null;
  let selectedStore = null;

  document.addEventListener("DOMContentLoaded", () => {
    init();
    bindVariantUpdates();
    bindFormSubmissions();
  });

  function init(root = document) {
    const drawer = root.querySelector("[data-reserve-in-store-drawer]");
    if (!drawer) {
      return;
    }

    activeDrawer = drawer;
    storesData = [];
    searchQuery = "";
    selectedStore = null;

    const wrapper = getWrapper(drawer);
    const storesScript = wrapper?.querySelector("#ReserveInStoreData");
    if (!storesScript) {
      return;
    }

    try {
      const parsedData = JSON.parse(storesScript.textContent.trim());
      storesData = parsedData.stores || [];
    } catch (error) {
      console.error("[ReserveInStore] Failed to parse store data:", error);
      return;
    }

    const searchInput = drawer.querySelector("[data-reserve-store-search]");
    const backButton = drawer.querySelector(".reserve-in-store-drawer__back");
    const changeButton = drawer.querySelector(
      ".reserve-in-store-drawer__change-btn",
    );
    const dialog = drawer.querySelector("dialog");

    if (searchInput) {
      searchInput.addEventListener("input", handleSearch);
    }

    drawer.addEventListener("click", handleDrawerClick);

    if (backButton) {
      backButton.addEventListener("click", showOverviewView);
    }

    if (changeButton) {
      changeButton.addEventListener("click", showOverviewView);
    }

    if (dialog) {
      dialog.addEventListener("close", showOverviewView);
    }

    renderStores();
  }

  function bindVariantUpdates() {
    document.addEventListener("variant:update", handleVariantUpdate);
  }

  function bindFormSubmissions() {
    document.addEventListener("submit", (event) => {
      const form = event.target.closest("form[data-reserve-in-store-form]");
      if (!form) {
        return;
      }

      event.preventDefault();
      handleFormSubmit(form);
    });
  }

  function handleVariantUpdate(event) {
    const section = event.target.closest(".shopify-section, dialog");
    const currentWrapper = section?.querySelector(".buy-buttons__reserve-in-store");
    const nextWrapper = event.detail?.data?.html?.querySelector(
      ".buy-buttons__reserve-in-store",
    );

    if (!currentWrapper || !nextWrapper) {
      return;
    }

    currentWrapper.innerHTML = nextWrapper.innerHTML;
    currentWrapper.hidden = !currentWrapper.querySelector(
      "[data-reserve-in-store-drawer]",
    );

    if (!currentWrapper.hidden) {
      init(currentWrapper);
    } else {
      activeDrawer = null;
      storesData = [];
      searchQuery = "";
      selectedStore = null;
    }
  }

  function handleSearch(event) {
    searchQuery = event.target.value.toLowerCase().trim();
    renderStores();
  }

  function renderStores() {
    const drawer = activeDrawer;
    if (!drawer) {
      return;
    }

    const container = drawer.querySelector('[data-reserve-view="overview"]');
    if (!container) {
      return;
    }

    const storeCount = drawer.querySelector("[data-reserve-store-count]");
    const filteredStores = storesData.filter((store) => {
      if (!searchQuery) {
        return true;
      }

      return (
        getSearchValue(store.name).includes(searchQuery) ||
        getSearchValue(store.address).includes(searchQuery) ||
        getSearchValue(store.locationId).includes(searchQuery)
      );
    });

    if (storeCount) {
      storeCount.textContent = getStoreCountText(storesData.length);
    }

    container
      .querySelectorAll(".reserve-in-store-drawer__store")
      .forEach((element) => element.remove());

    const noResultsEl = container.querySelector(
      ".reserve-in-store-drawer__no-results",
    );
    if (noResultsEl) {
      noResultsEl.remove();
    }

    filteredStores.forEach((store) => {
      container.appendChild(createStoreElement(store));
    });

    if (filteredStores.length === 0) {
      const noResults = document.createElement("div");
      noResults.className = "reserve-in-store-drawer__no-results";
      noResults.style.cssText =
        "text-align: center; padding: 40px 20px; color: #6b6b6b; font-size: 13px;";
      noResults.textContent = getEmptyStateText(storesData.length);
      container.appendChild(noResults);
    }
  }

  function createStoreElement(store) {
    const div = document.createElement("div");
    div.className = "reserve-in-store-drawer__store";

    const addressHtml = store.address
      ? `<div class="reserve-in-store-drawer__store-addr">${escapeHtml(store.address)}</div>`
      : "";

    div.innerHTML = `
      <div class="reserve-in-store-drawer__store-info">
        <div class="reserve-in-store-drawer__store-name">${escapeHtml(store.name)}</div>
        ${addressHtml}
      </div>
      <div class="reserve-in-store-drawer__store-side">
        <span class="reserve-in-store-drawer__stock">
          <span class="reserve-in-store-drawer__stock-dot"></span>
          På lager
        </span>
        <button
          type="button"
          class="reserve-in-store-drawer__reserve-btn"
          data-reserve-select-store
          data-metaobject-id="${escapeAttribute(store.id || "")}"
          data-location-id="${escapeAttribute(store.locationId || "")}"
          data-store-name="${escapeAttribute(store.name || "")}"
          data-address="${escapeAttribute(store.address || "")}"
          data-email="${escapeAttribute(store.email || "")}"
        >
          Reservér i butik
        </button>
      </div>
    `;

    return div;
  }

  function handleDrawerClick(event) {
    const reserveButton = event.target.closest("[data-reserve-select-store]");
    if (!reserveButton) {
      return;
    }

    event.preventDefault();
    const storeFromData = storesData.find(
      (store) =>
        store.id === reserveButton.dataset.metaobjectId ||
        store.locationId === reserveButton.dataset.locationId,
    );

    selectStore(
      storeFromData || {
        id: reserveButton.dataset.metaobjectId || "",
        locationId: reserveButton.dataset.locationId || "",
        name: reserveButton.dataset.storeName || "",
        address: reserveButton.dataset.address || "",
        email: reserveButton.dataset.email || "",
      },
    );
    showFormView();
  }

  function selectStore(store) {
    selectedStore = store;
    const drawer = activeDrawer;
    if (!drawer) {
      return;
    }

    const form = drawer.querySelector("form[data-reserve-in-store-form]");
    if (!form) {
      return;
    }

    setFieldValue(form, "location_metaobject_id", store.id);
    setFieldValue(form, "location_id", store.locationId);
    setFieldValue(form, "store_email", store.email);
    setFieldValue(form, "store_name", store.name);

    const selectedStoreBlock = drawer.querySelector(
      "[data-reserve-selected-store]",
    );
    const selectedStoreName = drawer.querySelector(
      "[data-selected-store-name]",
    );
    const selectedStoreAddress = drawer.querySelector(
      "[data-selected-store-address]",
    );

    if (selectedStoreName) {
      selectedStoreName.textContent = store.name;
    }

    if (selectedStoreAddress) {
      selectedStoreAddress.textContent = store.address;
      selectedStoreAddress.hidden = !store.address;
    }

    if (selectedStoreBlock) {
      selectedStoreBlock.hidden = false;
    }
  }

  function showFormView() {
    const drawer = activeDrawer;
    if (!drawer) {
      return;
    }

    const overviewView = drawer.querySelector('[data-reserve-view="overview"]');
    const formView = drawer.querySelector('[data-reserve-view="form"]');
    const backButton = drawer.querySelector(".reserve-in-store-drawer__back");

    if (overviewView) {
      overviewView.classList.add("is-pushed");
      overviewView.setAttribute("aria-hidden", "true");
    }

    if (formView) {
      formView.classList.add("is-active");
      formView.setAttribute("aria-hidden", "false");
    }

    if (backButton) {
      backButton.hidden = false;
    }
  }

  function showOverviewView() {
    const drawer = activeDrawer;
    if (!drawer) {
      return;
    }

    resetSubmittedForms(drawer);

    const overviewView = drawer.querySelector('[data-reserve-view="overview"]');
    const formView = drawer.querySelector('[data-reserve-view="form"]');
    const backButton = drawer.querySelector(".reserve-in-store-drawer__back");

    if (overviewView) {
      overviewView.classList.remove("is-pushed");
      overviewView.setAttribute("aria-hidden", "false");
    }

    if (formView) {
      formView.classList.remove("is-active");
      formView.setAttribute("aria-hidden", "true");
    }

    if (backButton) {
      backButton.hidden = true;
    }

    setFormState(drawer.querySelector("form[data-reserve-in-store-form]"), {
      loading: false,
      message: "",
    });
  }

  function handleFormSubmit(form) {
    const drawer = form.closest("[data-reserve-in-store-drawer]");
    const wrapper = getWrapper(drawer);

    // a submission is already in flight - ignore repeated clicks/Enter
    if (form.dataset.reserveSubmitting === "true") {
      return;
    }

    // already sent: the form stays locked until the drawer is reopened, so a
    // second click on a still-filled form cannot create a second reservation
    if (form.dataset.reserveDone === "true") {
      return;
    }

    if (!validateForm(form)) {
      return;
    }

    const payload = buildPayload(form, wrapper);
    const submitEvent = new CustomEvent(SUBMIT_EVENT, {
      bubbles: true,
      cancelable: true,
      detail: {
        payload,
        form,
        drawer,
        wrapper,
        setFormState: (state) => setFormState(form, state),
      },
    });

    // lock the button before handing off to the app embed, not inside it:
    // the embed clears the state again via setFormState when the request settles
    setFormState(form, { loading: true, message: "" });

    const handled = !form.dispatchEvent(submitEvent);

    if (!handled) {
      setFormState(form, {
        loading: false,
        message: "Reservationsmodulet er ikke tilsluttet endnu.",
        messageType: "error",
      });
    }
  }

  function validateForm(form) {
    if (!form.checkValidity()) {
      form.reportValidity();
      return false;
    }

    const locationId = getFieldValue(form, "location_id");
    if (!locationId) {
      setFormState(form, {
        loading: false,
        message: "Vælg en butik før du sender reservationen.",
        messageType: "error",
      });
      return false;
    }

    const phoneField = form.querySelector('[data-reserve-field="customer_phone"]');
    const phoneValue = phoneField?.value || "";
    const normalizedPhone = normalizeDanishPhone(phoneValue);

    if (!normalizedPhone) {
      setFormState(form, {
        loading: false,
        message: "Indtast et gyldigt dansk telefonnummer.",
        messageType: "error",
      });
      phoneField?.focus();
      return false;
    }

    setFormState(form, { message: "" });
    return true;
  }

  function getSubmissionId(form) {
    if (!form.dataset.reserveSubmissionId) {
      form.dataset.reserveSubmissionId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `rs-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }

    return form.dataset.reserveSubmissionId;
  }

  function resetSubmittedForms(root) {
    root
      ?.querySelectorAll('form[data-reserve-in-store-form][data-reserve-done="true"]')
      .forEach((form) => {
        delete form.dataset.reserveDone;
        delete form.dataset.reserveSubmissionId;
        setFormState(form, { loading: false, message: "" });
      });
  }

  function buildPayload(form, wrapper) {
    const context = getContext(wrapper);
    const phoneInput = getFieldValue(form, "customer_phone");

    return {
      customer: {
        name: getFieldValue(form, "customer_name"),
        email: getFieldValue(form, "customer_email"),
        phone: normalizeDanishPhone(phoneInput) || phoneInput,
      },
      location: selectedStore
        ? {
            metaobjectId: selectedStore.id || null,
            locationId: selectedStore.locationId || null,
            name: selectedStore.name || null,
            address: selectedStore.address || null,
            email: selectedStore.email || null,
          }
        : null,
      product: context?.product || null,
      variant: context?.variant || null,
      shop: context?.shop || null,
      meta: {
        sectionId: context?.sectionId || wrapper?.dataset.sectionId || null,
        pageUrl: window.location.href,
        submittedAt: new Date().toISOString(),
        // Stable for as long as the form holds this filled-in state, so a request
        // the network retried after the server already stored it is recognised
        // as the same submission instead of becoming a second reservation.
        submissionId: getSubmissionId(form),
      },
    };
  }

  function getContext(wrapper) {
    const contextScript = wrapper?.querySelector("#ReserveInStoreContext");
    if (!contextScript) {
      return null;
    }

    try {
      return JSON.parse(contextScript.textContent.trim());
    } catch (error) {
      console.error("[ReserveInStore] Failed to parse context:", error);
      return null;
    }
  }

  function getWrapper(drawer) {
    return drawer?.closest(".buy-buttons__reserve-in-store") || null;
  }

  function getFieldValue(form, fieldName) {
    const field = form.querySelector(`[data-reserve-field="${fieldName}"]`);
    return field?.value?.trim() || "";
  }

  function setFieldValue(form, fieldName, value) {
    const field = form.querySelector(`[data-reserve-field="${fieldName}"]`);
    if (field) {
      field.value = value || "";
    }
  }

  function setFormState(form, { loading = false, message = "", messageType = "info" } = {}) {
    if (!form) {
      return;
    }

    const submitButton = form.querySelector("[data-reserve-submit]");
    const messageElement = form.querySelector("[data-reserve-form-message]");

    form.dataset.reserveSubmitting = loading ? "true" : "false";

    const done = !loading && messageType === "success";

    if (done) {
      form.dataset.reserveDone = "true";
      delete form.dataset.reserveSubmissionId;
      CUSTOMER_FIELDS.forEach((name) => {
        const field = form.querySelector(`[name="${name}"]`);
        if (field) field.value = "";
      });
    }

    if (submitButton) {
      submitButton.disabled = !!loading || done;
      submitButton.setAttribute("aria-busy", loading ? "true" : "false");
    }

    if (!messageElement) {
      return;
    }

    if (message) {
      messageElement.textContent = message;
      messageElement.hidden = false;
      messageElement.dataset.type = messageType;
    } else {
      messageElement.textContent = "";
      messageElement.hidden = true;
      delete messageElement.dataset.type;
    }
  }

  function escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  function escapeAttribute(text) {
    return escapeHtml(text).replace(/"/g, "&quot;");
  }

  function getSearchValue(value) {
    return String(value || "").toLowerCase();
  }

  /**
   * Normalizes Danish phone input to E.164 (+45XXXXXXXX).
   * Accepts: 12345678, +4512345678, 4512345678, 0045 12 34 56 78
   */
  function normalizeDanishPhone(value) {
    const digits = String(value || "").replace(/\D/g, "");

    if (digits.length === 8 && isValidDanishSubscriberNumber(digits)) {
      return `+45${digits}`;
    }

    if (digits.length === 10 && digits.startsWith("45")) {
      const subscriber = digits.slice(2);
      if (isValidDanishSubscriberNumber(subscriber)) {
        return `+45${subscriber}`;
      }
    }

    if (digits.length === 12 && digits.startsWith("0045")) {
      const subscriber = digits.slice(4);
      if (isValidDanishSubscriberNumber(subscriber)) {
        return `+45${subscriber}`;
      }
    }

    return null;
  }

  function isValidDanishSubscriberNumber(digits) {
    return /^[2-9]\d{7}$/.test(digits);
  }

  function getStoreCountText(totalCount) {
    if (totalCount === 0) {
      return "Ingen butikker tilbyder afhentning";
    }

    return `På lager i ${totalCount} ${totalCount === 1 ? "butik" : "butikker"}`;
  }

  function getEmptyStateText(totalCount) {
    if (totalCount === 0) {
      return "Ingen butikker tilbyder afhentning for denne variant";
    }

    if (searchQuery) {
      return "Ingen butikker matcher din søgning";
    }

    return "Ingen butikker at vise";
  }

  window.ReserveInStore = {
    SUBMIT_EVENT,
    buildPayload(form) {
      const drawer = form.closest("[data-reserve-in-store-drawer]");
      return buildPayload(form, getWrapper(drawer));
    },
    getContext(wrapper) {
      return getContext(wrapper);
    },
    normalizeDanishPhone,
    setFormState,
    getForms(root = document) {
      return Array.from(root.querySelectorAll("form[data-reserve-in-store-form]"));
    },
  };
})();
